import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { AuthProvider } from "@/hooks/use-auth";
import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";
import { captureAttribution } from "@/lib/attribution";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "INFLOW Marketing" },
      { name: "description", content: "See How We Average 15-30 NEW QUALIFIED APPOINTMENTS Per Location A Month For Our Clients" },
      { name: "keywords", content: "Spinal Decompression, Regen, Knee Pain, Neuropathy" },
      { name: "author", content: "Faisal Mangal, CEO of INFLOW Marketing" },
      { property: "og:title", content: "INFLOW Marketing" },
      { property: "og:description", content: "See How We Average 15-30 NEW QUALIFIED APPOINTMENTS Per Location A Month For Our Clients" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "INFLOW Marketing" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "INFLOW Marketing" },
      { name: "twitter:description", content: "See How We Average 15-30 NEW QUALIFIED APPOINTMENTS Per Location A Month For Our Clients" },
      { name: "google-site-verification", content: "5frXqboB1kv_eobc7IUgHruH_GNpXLuI6vk9t7KXCFs" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "https://assets.cdn.filesafe.space/5WS7iGuKlqV8edmNNswQ/media/41d8899f-c1f3-4192-9aca-dd8ed31b6ab7.png" },
    ],

    scripts: [
      {
        // Third-party chat widget: injected on first user interaction or after
        // a 4s idle window, whichever comes first, so it never competes with
        // first paint.
        children: `(function(){var s='https://app.usecortana.ai/api/hub/v1/cmr5gtrlf002rkt04xousrruj';var done=false;function go(){if(done)return;done=true;['pointerdown','keydown','touchstart','scroll'].forEach(function(e){window.removeEventListener(e,go)});var t=document.createElement('script');t.src=s;t.defer=true;document.head.appendChild(t);}['pointerdown','keydown','touchstart','scroll'].forEach(function(e){window.addEventListener(e,go,{once:true,passive:true})});if('requestIdleCallback' in window){requestIdleCallback(function(){setTimeout(go,4000)},{timeout:6000})}else{setTimeout(go,4000)}})();`,
      },
      {
        children: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];}(window,document,'script');fbq('init','1178316203589856');fbq('track','PageView');var _fbLoad=function(){if(window.__fbLoaded)return;window.__fbLoaded=!0;var t=document.createElement('script');t.async=!0;t.src='https://connect.facebook.net/en_US/fbevents.js';document.head.appendChild(t);};if('requestIdleCallback' in window){requestIdleCallback(function(){setTimeout(_fbLoad,1200);},{timeout:3500});}else if(document.readyState==='complete'){setTimeout(_fbLoad,2500);}else{window.addEventListener('load',function(){setTimeout(_fbLoad,2500);});}`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://apply.inflow-agency.com/#organization",
              name: "INFLOW Marketing",
              alternateName: "Inflow Agency",
              url: "https://apply.inflow-agency.com/",
              logo: "https://assets.cdn.filesafe.space/5WS7iGuKlqV8edmNNswQ/media/41d8899f-c1f3-4192-9aca-dd8ed31b6ab7.png",
              founder: { "@type": "Person", name: "Faisal Mangal" },
              address: {
                "@type": "PostalAddress",
                addressLocality: "Houston",
                addressRegion: "TX",
                addressCountry: "US",
              },
              areaServed: "United States",
              description:
                "Performance-based patient acquisition for cash-pay specialty clinics offering spinal decompression, regenerative medicine, knee pain and neuropathy treatments.",
              makesOffer: {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Patient acquisition for cash-pay specialty clinics",
                  serviceType: "patient acquisition and appointment setting for cash-pay clinics",
                },
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://apply.inflow-agency.com/#website",
              url: "https://apply.inflow-agency.com/",
              name: "INFLOW Marketing",
              publisher: { "@id": "https://apply.inflow-agency.com/#organization" },
              inLanguage: "en-US",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: (props) => <ErrorComponent error={props.error as Error} reset={props.reset} />,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=1178316203589856&ev=PageView&noscript=1"
            alt=""
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  // Re-run on every URL change so a fresh ad click (new fbclid/UTM) on any
  // route is captured, and so _fbp/_fbc cookies set after the initial mount
  // by the Meta pixel script are picked up on the next navigation.
  const pathname = useRouterState({ select: (s) => s.location.href });
  useEffect(() => {
    captureAttribution();
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Outlet />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}
